
import { Agent } from './Agent';
import { Device } from './Device';
import { Call } from './Call';
import * as anCti from "./ancti";

export class Phone {

    primaryDevice?: Device;
    primaryCall?: Call;
    secondaryCall?: Call;
    calls: Call[] = [];
    speaker?: HTMLAudioElement;
    ringer?: HTMLAudioElement;

    // Webview sometimes fails when playing a stream.
    // To fix it we can simply wait some millis 
    // and then reassign the source-stream again.
    reassignElementSourceDelay = 0;

    private agent: Agent;
    private remoteVideo?: string|HTMLVideoElement;
    private localVideo?: string|HTMLVideoElement;
    private tones = new Map<string,string>();
    private releaseTimeout = 2000;
    private speakerDeviceId?: string;
    private ringerDeviceId?: string;
    

    constructor(agent: Agent) {
        this.agent = agent;
        this.speaker = new Audio();
        this.speaker.autoplay = true;
        this.ringer = new Audio();
        this.ringer.autoplay = true;
        this.ringer.loop = true;

        agent.on("call",(event) => this.onCall(event));
        agent.on("localstream",(event) => this.onLocalStream(event));
        agent.on("remotestream",(event) => this.onRemoteStream(event));
    }

    async setPrimaryDevice(deviceId: string) {
        if (this.primaryDevice && this.primaryDevice.deviceID!=deviceId) {
            try {
                await this.primaryDevice.monitorStop();
            } catch(error) {}
        }

        this.primaryDevice = this.agent.getDevice(deviceId);
        await this.primaryDevice.monitorStart({rtc: !!(this.primaryDevice as any).rtc});
        this.agent.notify("primarydevice",{primaryDevice:this.primaryDevice});
    }
    
    setToneUrl(name:anCti.StandardPhoneTone,url?:string) {
        this.agent.debug("setToneUrl",name,url);
        if (url) {
            this.tones.set(name,url);
        } else {
            this.tones.delete(name);
        }
    }

    setCamera(deviceId: string) {
        this.agent.debug("setCamera",deviceId);
        this.agent.setVideoDeviceId(deviceId);
        if (this.primaryCall?.videoOn) {
            this.primaryCall?.updateCall({audio:true, video:this.primaryCall.videoOn});
        }
    }

    setMicrophone(deviceId: string) {
        this.agent.debug("setMicrophone",deviceId);
        this.agent.setAudioDeviceId(deviceId);
        this.primaryCall?.updateCall({audio:true, video:this.primaryCall?.videoOn});
    }
   
    setSpeaker(deviceId: string) {
        this.agent.debug("setSpeaker",deviceId);
        this.speakerDeviceId = deviceId;
        if ("disable"==deviceId) {
            this.speaker = undefined;
        } else if (deviceId && this.speaker?.setSinkId) {
            this.speaker.setSinkId(deviceId).catch(error => {
                this.agent.error(`cannot set speaker: ${deviceId}`,error);
            });
        }
    }

    setRinger(deviceId: string) {
        this.agent.debug("setRinger",deviceId);
        this.ringerDeviceId = deviceId;
        if ("disable"==deviceId) {
            this.ringer = undefined;
        } else if (deviceId && this.ringer?.setSinkId) {
            this.ringer.setSinkId(deviceId).catch(error => {
                this.agent.error(`cannot set ringer: ${deviceId}`,error);
            });
        }
    }

    private ring(tone?:anCti.StandardPhoneTone|false) {
        this.ringer?.setSinkId && this.ringer.setSinkId(tone && this.ringerDeviceId || "");
        this.play(this.ringer,tone);
    }

    private inband(tone?:anCti.StandardPhoneTone|false) {
        this.speaker?.setSinkId && this.speaker.setSinkId(tone && this.speakerDeviceId || "");
        this.play(this.speaker,tone);
    }

    private play(audio?: HTMLAudioElement,tone?:anCti.StandardPhoneTone|false) {
        this.agent.debug("play",tone,"on",audio);
        if (audio) {
            if (tone) {
                let url = this.tones.get(tone);
                if (!url && tone.includes(".")) {
                    // tone is a file
                    url = tone;
                }
                if (url!=audio.src) {
                    if (url) {
                        audio.src = url;
                     } else {
                        audio.srcObject = null;
                     };
                    audio.loop = true;
                    audio.play().catch(error => {});
                }
            } else {
                // stop audio
                audio.loop = false;
                audio.pause();
                audio.src = "";
                audio.srcObject = null;
            }
        }
    }

    private getVideoElement(element?:HTMLVideoElement|string): HTMLVideoElement|undefined {
        if ((typeof element)=="string") {
            return document.querySelector(element as string) as HTMLVideoElement;
        } else {
            return element as HTMLVideoElement;
        }
    }

    setRemoteVideoElement(element?:HTMLVideoElement|string) {
        this.agent.debug("setRemoteVideoElement",element);
        this.remoteVideo = element;
        this.setVideoStream("remoteVideo",this.remoteVideo,this.primaryCall?.remoteVideoStream);
    }

    setLocalVideoElement(element?:HTMLVideoElement|string) {
        this.agent.debug("setLocalVideoElement",element);
        this.localVideo = element;
        this.setVideoStream("localVideo",this.localVideo,this.primaryCall?.localVideoStream);
    }

    private async setVideoStream(type:string,element?:string|HTMLVideoElement,stream?:MediaStream) {
        const video = this.getVideoElement(element);
        if (video) {

            // if remote-stream is not receiving data we hide the remote-video
            if (stream && stream==this.primaryCall?.remoteVideoStream) {
                if (this.primaryCall.state!="connected") {
                    this.agent.debug("ignore remote-video in state",this.primaryCall.state);
                    stream = undefined;
                } else {
                    const transceiver = this.getTransceiver(this.primaryCall,"video");
                    if (!transceiver?.direction?.includes("recv")) {
                        this.agent.debug("ignore remote-video in transceiver direction",transceiver?.direction);
                        stream = undefined;
                    } else if (transceiver?.receiver?.track?.muted) {
                        this.agent.debug("ignore remote-video of muted track",transceiver.receiver.track);
                        stream = undefined;
                    }
                }
            }
    
            this.setMediaStream(type,video,stream);
            video.style.display = stream?"":"none";       
        }
    }
    
    private setMediaStream(type:string,element?:HTMLMediaElement,stream?:MediaStream) {
        if (element && element.srcObject!=stream) {
            element.srcObject =  stream || null;

            // android webview has some serious problems and often raises an error.
            // For now the solution is just to wait a few millis and then update the srcObject.
            if (stream && this.reassignElementSourceDelay) {
                element.onerror = () => {
                    if (element.srcObject) {
                        this.agent.debug(`schedule source-reset for ${type} in ${this.reassignElementSourceDelay} ms`);
                        clearTimeout((element as any).resetTimeout);
                        (element as any).resetTimeout = setTimeout(() => {
                            if (element.srcObject==stream) {
                                this.agent.debug(`source-reset for ${type}`);
                                element.srcObject = stream;
                            }
                        },this.reassignElementSourceDelay);
                    }
                }                        
            }
        }
    }

    private getTransceiver(call:Call,media:string): RTCRtpTransceiver|undefined {
       return call?.pc?.getTransceivers().find(tc => tc.sender?.track?.kind==media || tc.receiver?.track?.kind==media);
    }

    private onCall(event:any) {
        const call /*: anCti.Call*/ = event.call;

        // ignore call-event for other devices
        if (call.device!=this.primaryDevice) return;

        let ended = false;
        switch (call.state) {
            case "fail": // snom?
            case "failed":
            case "null":
                ended = true;
                break;
            case "alerting":
                call.avatar ||= event.content?.callingDevice?.avatar;
                break;
        }

         // update list of calls
        if (ended) {
            this.calls = this.calls.filter(c => c != call);
    
            // keep track for some seconds to provide information about the ended call
            setTimeout(() => {
                if (this.primaryCall == call) {
                    this.inband();
                    this.primaryCall = undefined;
                    this.agent.notify("primarycall", {call: undefined});
                }
            }, this.releaseTimeout);
    
        } else if (!this.calls.includes(call)) {
            this.calls.push(call);
        }
    
        // select primary call
        const oldPrimaryCall = this.primaryCall;
        this.primaryCall = this.calls.find(c => c.state == 'calling')
            || this.calls.find(c => c.state == 'connected')
            || this.calls.find(c => c.state == 'alerting')
            || this.calls?.[0];

        if (oldPrimaryCall && this.primaryCall!=oldPrimaryCall) {
            // last primary call becomes secondary
            this.secondaryCall = oldPrimaryCall;
        } else {
            // search for last call in list
            this.secondaryCall = undefined;
            this.calls.forEach(c => {
                if (c!=this.primaryCall) this.secondaryCall = c;
            });
        }

        // handle inband tones
        if (this.primaryCall?.state == "calling") {
            this.inband("ringback");
        } else if (ended) {
            this.inband("busy");
        } else {
            this.inband();
        }
    
        // handle ringer:
        // Don't start ringing if we will answer automatically, seems Jabras have problems
        // when playing short tones which sounds like a fart...
        if (this.calls.find(c => c.state == 'alerting' && !c.autoAnswer)) {
            this.ring("ringtone");
        } else {
            this.ring(false);
        }
     
        if (call==this.primaryCall || !this.primaryCall || this.primaryCall!=oldPrimaryCall) {
            // tell clients that the call and streams changed
            this.agent.notify("primarycall",  {call: this.primaryCall});
        }
    
        // also update streams if we are "connected". Might be that
        // we were alerting before and now have to activate the remote-view
        if (this.primaryCall != oldPrimaryCall || call.state == "connected") {
            // ensure we setup the speaker with the remote-stream of the current primary call
            if (this.speaker) {
                this.play(this.speaker); // stop inband
                this.setMediaStream("speaker",this.speaker,this.primaryCall?.remoteAudioStream);
            }
            this.setVideoStream("remoteVideo",this.remoteVideo,this.primaryCall?.remoteVideoStream);
            this.agent.notify("primaryremotestream", { 
                call, 
                pc: call?.pc,
                stream: call?.remoteStream,
                audioStream: call?.remoteAudioStream,
                videoStream: call?.remoteVideoStream,
            });
            this.setVideoStream("localVideo",this.localVideo,this.primaryCall?.localVideoStream);
            this.agent.notify("primarylocalstream", { 
                call, 
                pc: call?.pc,
                stream: call?.localStream,
                audioStream: call?.localAudioStream,
                videoStream: call?.localVideoStream,
            });
        }
       
    }

    private onLocalStream(event:any) {
        let call = event.call;
    
        if (call.localTracks) {
            if (call.localTracks.video) {
                call.videoOn = call.localTracks.video.enabled;
            } else {
                call.videoOn = false;
            }
        
            const displayTrack = call.localTracks.display;
            if (displayTrack) {
                call.displayOn = displayTrack.enabled;
                if (!displayTrack.onended) {
                    displayTrack.onended = () => {
                        if (call==this.primaryCall && displayTrack==call.localTracks.display && call.displayOn) {
                            call.updateCall({audio:true,display:false,video:false});
                        }
                    };
                }
            } else {
                call.displayOn = false;
            }
        }
    
        if (call == this.primaryCall) {
            this.setVideoStream("localVideo",this.localVideo,call?.localVideoStream);
            this.agent.notify("primarylocalstream", { 
                call, 
                pc: call?.pc,
                stream: call?.localStream,
                audioStream: call?.localAudioStream,
                videoStream: call?.localVideoStream,
            });
    
            if (call.state == "held") {
                // if state changes from 'held' to 'connected' the
                // video-stream should be updated as well
                this.updatePrimaryRemoteStream();
            }
        }
    }

    private onRemoteStream(event:any) {
        const call = event.call;
        if (call==this.primaryCall) {
            this.updatePrimaryRemoteStream();
        }
    }
      
    private updatePrimaryRemoteStream() {
        this.setMediaStream("speaker",this.speaker,this.primaryCall?.remoteAudioStream);
        this.setVideoStream("remoteVideo",this.remoteVideo,this.primaryCall?.remoteVideoStream);
        this.agent.notify("primaryremotestream", { 
            call: this.primaryCall, 
            pc: this.primaryCall?.pc,
            stream: this.primaryCall?.remoteStream,
            audioStream: this.primaryCall?.remoteAudioStream,
            videoStream: this.primaryCall?.remoteVideoStream,
        });
    }

}

